import { array, iots, option, pipe } from '@code-expert/prelude';
import { CourseUrl, ExamId, GroupId, SemesterId, semesterIdEq } from '/imports/domain';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';
import { UiState } from '/imports/utils/UiState';

export const pageParamUiStates = {
  semester: UiState.valueProp('currentSemester', SemesterId),
  courseUrl: UiState.valueProp('currentCourse', CourseUrl),
  groupId: UiState.valueProp('currentGroupId', GroupId),
  examId: UiState.valueProp('currentExamId', ExamId),
};

const PageParams = iots.exact(
  iots.partial({
    semester: SemesterId,
    courseUrl: CourseUrl,
    groupId: GroupId,
    examId: ExamId,
  }),
);

type PageParams = iots.TypeOf<typeof PageParams>;

export const setPageParamsUiState = ({ semester, courseUrl, groupId, examId }: PageParams) => {
  if (semester != null) {
    pageParamUiStates.semester.set(semester);
  } else {
    pageParamUiStates.semester.clear();
  }

  if (courseUrl != null) {
    pageParamUiStates.courseUrl.set(courseUrl);
  }

  if (groupId != null) {
    pageParamUiStates.groupId.set(groupId);
  }

  if (examId != null) {
    pageParamUiStates.examId.set(examId);
  }
};

export const useGetPageParamsUiState = ({
  includeHiddenSemesters,
}: {
  includeHiddenSemesters?: boolean;
} = {}): PageParams => {
  const { semesters, visibleSemesters } = useGlobalContext();

  const semester = pipe(
    option.fromNullable(pageParamUiStates.semester.get()),
    option.filter((s) =>
      pipe(
        semesters,
        array.exists(
          ({ key }) =>
            semesterIdEq.equals(key, s) &&
            (includeHiddenSemesters || array.elem(semesterIdEq)(key, visibleSemesters)),
        ),
      ),
    ),
    option.toUndefined,
  );

  return {
    semester,
    courseUrl: pageParamUiStates.courseUrl.get(),
    groupId: pageParamUiStates.groupId.get(),
    examId: pageParamUiStates.examId.get(),
  };
};
